'use client';

import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import Flex from '@/components/Flex';
import cx from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { Search } from '@/components/Search';
import { ROUTES } from '@/constants';
import { useCustomAuth } from '@/contexts/AuthContext';
import { ICONS } from '@/svgs/Icons';
import mainLogo from '@/svgs/logos/rollinews.svg';

import { useGoBack } from '@/hooks';
import { CustomButton, EButtonVariants } from '../../CustomButton';
import { EButtonSizes } from '../../CustomButton/CustomButton.types';
import { CustomIcon } from '../../CustomIcon';
import { useHeader } from '../Header.context';
import styles from '../Header.module.scss';
import { EHeaderType } from '../Header.types';

export interface IHMobileProps {
  children?: React.ReactNode;
  footerAction?: React.ReactNode;
  canGoBack?: boolean;
  type?: EHeaderType;
  userData?: {
    name: string;
    user: string;
  };
}

function getHeaderTypeByPathname(pathname: string) {
  switch (pathname) {
    case ROUTES.HOME:
      return EHeaderType.HOME;
    case ROUTES.PROFILE:
      return EHeaderType.USER;
    case ROUTES.SEARCH:
      return EHeaderType.SEARCH;
    default:
      return EHeaderType.DEFAULT;
  }
}

export const HMobile: React.FC<{
  openModal: () => void;
  unreadNotifications: number;
}> = ({ openModal, unreadNotifications }) => {
  const { goBack } = useGoBack();
  const { push, replace } = useRouter();

  const pathname = usePathname();
  const auth = useCustomAuth();

  const { title, showSearchIcon, searchValue } = useHeader();
  const type = getHeaderTypeByPathname(pathname);

  const isFeedRoute = pathname.includes(ROUTES.FEED);
  const isSearchRoute = pathname.includes(ROUTES.SEARCH);

  const handleSearch = (params: URLSearchParams) => {
    replace(`${pathname}?${params.toString()}`);
  };

  const handleReset = () => {
    replace(pathname);
  };

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      gap="16px"
      className={styles.header_mobile}
    >
      {type === EHeaderType.HOME ? (
        <>
          <Link href={ROUTES.HOME} className={styles.logo}>
            <Image alt="Rollin News" src={mainLogo} />
          </Link>
          <Flex gap="16px">
            <button
              className={cx(styles.iconButton, styles.notifications)}
              onClick={openModal}
            >
              {unreadNotifications ? (
                <span className="chip">{unreadNotifications}</span>
              ) : null}
              {ICONS.notifications.default}
            </button>
            <button
              className={cx(styles.iconButton, styles.search)}
              onClick={() => push(ROUTES.SEARCH)}
            >
              {ICONS.search.default}
            </button>
          </Flex>
        </>
      ) : type === EHeaderType.USER ? (
        <>
          {title}
          <Flex gap="24px">
            <CustomButton
              title="Ir a la home"
              onClick={() => push(ROUTES.HOME)}
              variant={EButtonVariants.MINIMAL}
              size={EButtonSizes.MD}
              className={styles.iconButton}
            >
              {ICONS.home.default}
            </CustomButton>
            <CustomButton
              variant={EButtonVariants.MINIMAL}
              size={EButtonSizes.MD}
              onClick={() => auth.onLogout()}
              title="Cerrar sesión"
              className={styles.iconButton}
            >
              <CustomIcon icon={faSignOutAlt} />
            </CustomButton>
          </Flex>
        </>
      ) : (
        <>
          <Flex.Item grow={1}>
            <Flex
              gap="16px"
              alignItems="center"
              className={cx({ [styles.overlay]: isFeedRoute })}
            >
              <CustomButton
                title="Ir atrás"
                onClick={goBack}
                variant={EButtonVariants.MINIMAL}
                size={EButtonSizes.MD}
                className={styles.iconButton}
              >
                {ICONS.back.default}
              </CustomButton>
              {isSearchRoute ? (
                <Search
                  onSearch={handleSearch}
                  onReset={handleReset}
                  value={searchValue}
                  className={styles.customInput}
                />
              ) : (
                title && (
                  <span className={cx('label-md', styles.title)}>{title}</span>
                )
              )}
            </Flex>
          </Flex.Item>
          {showSearchIcon && !isFeedRoute && !isSearchRoute && (
            <CustomButton
              title="Buscar"
              onClick={() => push(ROUTES.SEARCH)}
              variant={EButtonVariants.MINIMAL}
              size={EButtonSizes.MD}
              className={styles.iconButton}
            >
              {ICONS.search.default}
            </CustomButton>
          )}
        </>
      )}
    </Flex>
  );
};
